import React, { useMemo, useState } from 'react';
import { collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { CurrentLeague, LeagueCatalogDoc, LeagueControl, Player } from '@/types';
import { Check, Eye, EyeOff, Plus, RefreshCw, Save, Search } from 'lucide-react';

type Controls = Record<string, LeagueControl>;

const inputCls =
  'w-full px-3 py-2 border border-tiger-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-tiger-primary-black focus:border-transparent transition text-sm';

// Mirror ls-sync's resolution so the public site reflects a change immediately,
// before the nightly sync recomputes the same result.
function applyControls(leagues: CurrentLeague[], controls: Controls): CurrentLeague[] {
  return leagues.map((l) => {
    const ctl = controls[String(l.lsLeagueId)];
    if (l.ambiguous) {
      if (ctl?.lsBowlerId != null) {
        return l.lsBowlerId === ctl.lsBowlerId
          ? { ...l, ambiguous: false, hidden: ctl.visible === false }
          : { ...l, hidden: true }; // the other same-name row -> hide (sync drops it next run)
      }
      return l; // still unresolved -> stays hidden via ambiguous
    }
    return { ...l, hidden: ctl?.visible === false };
  });
}

const StatusChip: React.FC<{ l: CurrentLeague }> = ({ l }) => {
  const [text, cls] = l.ambiguous
    ? ['Needs review', 'bg-amber-100 text-amber-800']
    : l.hidden
      ? ['Hidden', 'bg-tiger-neutral-200 text-tiger-neutral-600']
      : ['Public', 'bg-green-100 text-green-800'];
  return <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cls}`}>{text}</span>;
};

export const LeaguePanel: React.FC<{ player: Player }> = ({ player }) => {
  const [controls, setControls] = useState<Controls>(player.leagueControls || {});
  const [leagues, setLeagues] = useState<CurrentLeague[]>(player.currentLeagues || []);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [dirty, setDirty] = useState(false);

  // add-a-league browser
  const [showAdd, setShowAdd] = useState(false);
  const [catalog, setCatalog] = useState<LeagueCatalogDoc[] | null>(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [pickedLeagueId, setPickedLeagueId] = useState('');
  const [bowlerSearch, setBowlerSearch] = useState('');

  const setControl = (leagueId: number, patch: LeagueControl | null) => {
    setControls((prev) => {
      const next = { ...prev };
      if (patch === null) delete next[String(leagueId)];
      else next[String(leagueId)] = { ...next[String(leagueId)], ...patch };
      return next;
    });
    setDirty(true);
  };

  // Group current leagues by lsLeagueId so a same-name collision (2 rows) becomes one picker.
  const groups = useMemo(() => {
    const m = new Map<number, CurrentLeague[]>();
    for (const l of leagues) {
      if (!m.has(l.lsLeagueId)) m.set(l.lsLeagueId, []);
      m.get(l.lsLeagueId)!.push(l);
    }
    return [...m.entries()];
  }, [leagues]);

  const loadCatalog = async () => {
    if (catalog || loadingCatalog) return;
    setLoadingCatalog(true);
    try {
      const snap = await getDocs(collection(db, 'leagueCatalog'));
      const docs = snap.docs.map((d) => d.data() as LeagueCatalogDoc).sort((a, b) => a.league.localeCompare(b.league));
      setCatalog(docs);
    } catch (e) {
      console.error('catalog load failed', e);
      setMessage('Could not load the league catalog, run ls-sync once to build it.');
    } finally {
      setLoadingCatalog(false);
    }
  };

  const claimBowler = (leagueId: number, lsBowlerId: number) => {
    setControl(leagueId, { lsBowlerId, visible: true, claimed: true });
    setShowAdd(false);
    setPickedLeagueId('');
    setBowlerSearch('');
    setMessage('League added, it appears on the site after the next sync. Save to record it.');
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const patched = applyControls(leagues, controls);
      await updateDoc(doc(db, 'players', player.id), {
        leagueControls: controls,
        currentLeagues: patched, // optimistic: public site updates now; sync keeps it consistent
        updatedAt: serverTimestamp(),
      });
      setLeagues(patched);
      setDirty(false);
      setMessage('Saved.');
    } catch (e) {
      console.error('league save failed', e);
      setMessage('Save failed. Check console.');
    } finally {
      setSaving(false);
    }
  };

  const pickedLeague = catalog?.find((c) => String(c.lsLeagueId) === pickedLeagueId);
  const alreadyTracked = new Set(leagues.map((l) => l.lsLeagueId));

  return (
    <section className="bg-white rounded-2xl shadow-tiger p-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-2xl tracking-wide text-tiger-primary-black uppercase">Leagues</h2>
        <button
          onClick={handleSave}
          disabled={saving || !dirty}
          className="inline-flex items-center gap-2 px-4 py-2 bg-tiger-primary-black text-white rounded-lg font-bold text-sm hover:bg-tiger-neutral-800 transition-colors disabled:opacity-40"
        >
          <Save className="w-4 h-4" /> {saving ? 'Saving…' : dirty ? 'Save leagues' : 'Saved'}
        </button>
      </div>
      <p className="text-sm text-tiger-neutral-500 mt-1 mb-4">
        Every LeagueSecretary league synced for {player.name}. Show or hide each one on the public site, pick the right
        bowler when a name is shared (parent + kid), or add a league the auto-match missed. Changes stick through the
        nightly sync.
      </p>

      {message && (
        <div
          className={`px-4 py-2 rounded-lg font-bold text-sm mb-4 ${
            message === 'Saved.' ? 'bg-green-50 text-green-800' : 'bg-blue-50 text-blue-800'
          }`}
        >
          {message}
        </div>
      )}

      {groups.length === 0 && (
        <p className="text-sm text-tiger-neutral-500 italic mb-4">
          No leagues synced yet. Use “Add a league” below, or check that this bowler's name matches LeagueSecretary.
        </p>
      )}

      <div className="space-y-3">
        {groups.map(([leagueId, rows]) => {
          const ctl = controls[String(leagueId)];
          const chosen = ctl?.lsBowlerId != null ? rows.find((r) => r.lsBowlerId === ctl.lsBowlerId) : null;
          const head = chosen || rows[0];
          const ambiguous = rows.some((r) => r.ambiguous) && ctl?.lsBowlerId == null;
          return (
            <div key={leagueId} className="border border-tiger-neutral-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-tiger-primary-black">{head.league}</span>
                    <StatusChip l={applyControls([head], controls)[0]} />
                  </div>
                  <div className="text-xs text-tiger-neutral-500 mt-0.5">
                    {head.center} · {head.season}
                    {head.dayOfWeek ? ` · ${head.dayOfWeek}` : ''}
                  </div>
                </div>
                {!ambiguous && (
                  <button
                    onClick={() => setControl(leagueId, { visible: ctl?.visible === false ? true : false })}
                    className="inline-flex items-center gap-1.5 text-sm font-bold text-tiger-neutral-600 hover:text-tiger-primary-black"
                  >
                    {ctl?.visible === false ? (
                      <>
                        <EyeOff className="w-4 h-4" /> Hidden, show
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4" /> Public, hide
                      </>
                    )}
                  </button>
                )}
              </div>

              {ambiguous ? (
                <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm font-bold text-amber-900 mb-2">
                    Two bowlers named {player.name} in this roster, which one is {player.name}?
                  </p>
                  <div className="space-y-2">
                    {rows.map((r) => (
                      <button
                        key={r.lsBowlerId}
                        onClick={() => setControl(leagueId, { lsBowlerId: r.lsBowlerId, visible: true })}
                        className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 bg-white border border-amber-200 rounded-lg hover:border-tiger-primary-black transition-colors"
                      >
                        <span className="text-sm">
                          <span className="font-bold">{r.team || 'No team'}</span>
                          <span className="text-tiger-neutral-500">
                            {' '}
                            · avg {r.currentAverage} · {r.games} games · id {r.lsBowlerId}
                          </span>
                        </span>
                        <Check className="w-4 h-4 text-tiger-neutral-300" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-tiger-neutral-600 mt-2">
                  avg <span className="font-bold">{head.currentAverage}</span> · {head.games} games · HG{' '}
                  {head.highGame} · {head.improvement >= 0 ? '+' : ''}
                  {head.improvement} vs entering
                  {ctl?.claimed && <span className="ml-2 text-xs text-tiger-neutral-400">(added by you)</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add a league */}
      <div className="mt-4">
        {!showAdd ? (
          <button
            onClick={() => {
              setShowAdd(true);
              loadCatalog();
            }}
            className="inline-flex items-center gap-2 px-4 py-2 border-2 border-dashed border-tiger-neutral-300 rounded-lg font-bold text-tiger-neutral-600 hover:border-tiger-primary-black hover:text-tiger-primary-black transition-colors"
          >
            <Plus className="w-4 h-4" /> Add a league
          </button>
        ) : (
          <div className="border border-tiger-neutral-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-tiger-neutral-700">Add a league from LeagueSecretary</h3>
              <button
                onClick={loadCatalog}
                className="inline-flex items-center gap-1 text-xs text-tiger-neutral-500 hover:text-tiger-primary-black"
                title="Reload catalog"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingCatalog ? 'animate-spin' : ''}`} /> refresh
              </button>
            </div>

            {loadingCatalog && <p className="text-sm text-tiger-neutral-500">Loading leagues…</p>}
            {catalog && catalog.length === 0 && (
              <p className="text-sm text-tiger-neutral-500">
                Catalog empty, run <code className="bg-tiger-neutral-100 px-1 rounded">node ls-sync.mjs</code> once to
                build it.
              </p>
            )}

            {catalog && catalog.length > 0 && (
              <>
                <select className={inputCls} value={pickedLeagueId} onChange={(e) => setPickedLeagueId(e.target.value)}>
                  <option value="">Choose a league…</option>
                  {catalog.map((c) => (
                    <option key={c.lsLeagueId} value={String(c.lsLeagueId)}>
                      {c.league} · {c.center} ({c.season}){alreadyTracked.has(c.lsLeagueId) ? ' ✓ tracked' : ''}
                    </option>
                  ))}
                </select>

                {pickedLeague && (
                  <>
                    <div className="relative">
                      <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-tiger-neutral-400" />
                      <input
                        className={`${inputCls} pl-9`}
                        placeholder={`Search ${pickedLeague.bowlers.length} bowlers…`}
                        value={bowlerSearch}
                        onChange={(e) => setBowlerSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <div className="max-h-64 overflow-y-auto space-y-1">
                      {pickedLeague.bowlers
                        .filter((b) => b.name.toLowerCase().includes(bowlerSearch.toLowerCase()))
                        .slice(0, 40)
                        .map((b) => (
                          <button
                            key={b.lsBowlerId}
                            onClick={() => claimBowler(pickedLeague.lsLeagueId, b.lsBowlerId)}
                            className="w-full text-left flex items-center justify-between gap-3 px-3 py-2 border border-tiger-neutral-200 rounded-lg hover:border-tiger-primary-black transition-colors"
                          >
                            <span className="text-sm">
                              <span className="font-bold">{b.name}</span>
                              <span className="text-tiger-neutral-500">
                                {' '}
                                · {b.team || 'No team'} · avg {b.average} · {b.games} games
                              </span>
                            </span>
                            <Plus className="w-4 h-4 text-tiger-neutral-300" />
                          </button>
                        ))}
                    </div>
                    <p className="text-xs text-tiger-neutral-400">
                      Pick this bowler's row. It's attributed to {player.name} on the next sync (or run ls-sync now).
                    </p>
                  </>
                )}
              </>
            )}

            <button onClick={() => setShowAdd(false)} className="text-sm text-tiger-neutral-500 hover:text-tiger-primary-black">
              Cancel
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
